import React, { useEffect, useState } from 'react';
import { message, Table, Upload, UploadProps } from 'antd';
import { RcFile } from 'antd/es/upload';
import { useNavigate } from 'react-router-dom';
import { api, endpoints } from '../../api';
import ExpertiseListModalComponent from '../../components/ExpertiseListModalComponent';
import { routerPath } from '../../router/routerPath';
import {
  BlueLayout,
  CustomButton,
  CustomTableContainer,
  DownloadCol,
  FormBlock,
  GridItem,
  ManualContainer,
  PagesContainerUI,
} from '../../styles/styled';
import { getBase64 } from '../../utils/convertToBase64';
import { StatusType } from '../ApplicationPage/types';
import { LocaleExpertisesType } from '../ExpertExpertisePage/ExpertExpertisePage';
import { ExpertisesType, ExpertiseTextbookType, ExpertiseType } from '../ExpertisePage/types';
import { ExpertiseStatusSvgGenerator } from '../ExpertProfilePage/components/ExpertProfileComponent/ExpertProfileComponent';
import { FileDataType } from '../NewApplicationPage/types';
import { FileResponseType } from '../NewDocumentCategory/components/AddFIleForm/types';
import { ExpertiseExpertListType } from '../PovsExpertisePage/types';

export const PovsExpertisesPage = () => {
  const [currentExpertisesState, setCurrentExpertises] = useState<LocaleExpertisesType>([]);
  const [openModal, setOpen] = useState<boolean>(false);
  const [currentId, setCurrentId] = useState<number | undefined>();
  const [fileLoadState, setFileLoad] = useState<FileDataType | null>(null);
  const [currentExpertiseListId, setCurrentExpertiseListId] = useState<number | undefined>();
  const navigate = useNavigate();
  const [fileLoad, setLoad] = useState(false);
  const [currentPovCode, setCurrentPovCode] = useState<string | undefined>();
  const [chosenExpertiseList, setChosenExpertiseList] = useState<ExpertiseType | undefined>();
  const [updateData, setUpdate] = useState<boolean>(true);

  const handleEl: UploadProps['onChange'] = (info) => {
    if (info.file.status === 'error') setLoad(true);

    getBase64(info.file.originFileObj as RcFile, (url) =>
      setFileLoad({
        base64: url.split('base64,')[1],
        title: info.file.name as string,
        ext: info.file.name.split('.')[1],
      }),
    );

    return;
  };

  useEffect(() => {
    if (fileLoad && currentExpertiseListId)
      api.post<FileResponseType>(endpoints.addFile(), fileLoadState).then((el) =>
        api
          .put(endpoints.loadFinalList(currentExpertiseListId), {
            final_exp_file: { id: el.data.file_id },
          })
          .then(() => {
            message.success('Итоговый ЭЛ успешно загружен');

            setUpdate(true);
            setLoad(false);
          })
          .catch(() => message.error('Произошла ошибка при загрузке файла')),
      );

    // eslint-disable-next-line
  }, [fileLoadState]);

  useEffect(() => {
    if (!updateData) return;

    (async () => {
      try {
        const expertisesResponse = await api.get<ExpertisesType>(endpoints.getCurrentExpertExpertises());

        setCurrentExpertises(
          expertisesResponse.data.map((el, index) => {
            return { ...el, key: index };
          }),
        );

        setUpdate(false);
      } catch (err) {}
    })();
  }, [updateData]);

  const expertisesColumns = [
    {
      title: '№',
      dataIndex: 'key',
      key: 'key',
      render: (value: number) => <p>{value + 1}</p>,
    },
    {
      title: 'Наименование учебника',
      key: 'title',
      dataIndex: 'textbook',
      render: (textbook: ExpertiseTextbookType) => <p>{textbook?.title}</p>,
    },
    {
      title: 'Класс',
      key: 'school_classes',
      dataIndex: 'textbook',
      render: (textbook: ExpertiseTextbookType) => (
        <p>{textbook?.school_classes.length > 0 && textbook?.school_classes[0].title}</p>
      ),
    },
    {
      title: 'Автор (авторский коллектив)',
      key: 'author',
      dataIndex: 'textbook',
      render: (textbook: ExpertiseTextbookType) => <p>{textbook?.author}</p>,
    },
    {
      title: 'Наименование учебного пособия',
      dataIndex: 'textbook',
      key: 'manual_title',
      render: (textbook: ExpertiseTextbookType) => {
        return textbook?.text_book_edu_man?.map(
          (el, idx) => el.title && <ManualContainer key={`${textbook.key}-${idx}`}>{el.title}</ManualContainer>,
        );
      },
    },
    {
      title: 'Автор (авторский коллектив) учебного пособия',
      dataIndex: 'textbook',
      key: 'manual_author',
      render: (textbook: ExpertiseTextbookType) => {
        return textbook?.text_book_edu_man?.map(
          (el, idx) => el.fio && <ManualContainer key={`${textbook.key}-${idx}`}>{el.fio}</ManualContainer>,
        );
      },
    },
    {
      title: 'Наименование издательства',
      key: 'publisher',
      dataIndex: 'textbook',
      render: (textbook: ExpertiseTextbookType) => <p>{textbook?.publisher?.title}</p>,
    },
    {
      title: 'Дата загрузки итогового ЭЛ',
      dataIndex: 'expertise',
      key: 'date',
      render: (expertise_list, expertise: ExpertiseType) =>
        expertise?.expertise_list?.date_loading_final_list && (
          <GridItem justify="flex-start" align="flex-start" gap="2px" direction="row" wrap="nowrap">
            <p>{expertise?.expertise_list?.date_loading_final_list?.split('T')[0].split('-').reverse().join('.')}</p>
          </GridItem>
        ),
    },
    {
      title: 'Статус экспертизы',
      key: 'status',
      dataIndex: 'status',
      render: (status: StatusType) => ExpertiseStatusSvgGenerator(status),
    },
    {
      title: 'Статус ЭЛ',
      dataIndex: 'expertise_list',
      key: 'status',
      render: (status: ExpertiseExpertListType) => {
        return <p>{status?.status?.name}</p>;
      },
    },
    {
      title: 'Экспертиза',
      dataIndex: 'id',
      key: 'expertise',
      render: (id: number, expertise: ExpertiseType) => (
        <GridItem gap="5px" justify="flex-start" align="center">
          <CustomButton
            onClick={() => {
              setChosenExpertiseList(expertise);

              if (expertise?.expertise_list?.status?.code === 'complete') {
                setCurrentId(expertise?.expertise_list?.id);
                setOpen(true);
                setCurrentPovCode(undefined);
              } else if (expertise?.expertise_list?.status?.code === 'refactoring') {
                setCurrentId(expertise?.expertise_list?.id);
                setCurrentPovCode('refactoring');
                setOpen(true);
              } else if (expertise?.expertise_list?.status?.code === 'sent') {
                setCurrentId(expertise?.expertise_list?.id);
                setCurrentPovCode(undefined);
                setOpen(true);
              } else {
                navigate(routerPath.povsLkExpertise + id);
                setCurrentPovCode(undefined);
              }
            }}
            lined
            height="25px"
            padding="0 8px"
            width="120px"
            fontsize="10px !important"
            nowrap
          >
            Экспертный лист
          </CustomButton>
          {expertise?.expertise_list?.final_exp_file && (
            <a href={expertise.expertise_list.final_exp_file?.file_path} download target="_blank" rel="noreferrer">
              <CustomButton height="25px" padding="0 8px" width="120px" fontsize="10px !important" nowrap>
                Итоговый эл
              </CustomButton>
            </a>
          )}
          {expertise?.expertise_list?.status?.code === 'complete' && (
            <button className="empty-button" onClick={() => setCurrentExpertiseListId(expertise?.expertise_list?.id)}>
              <DownloadCol noBorder span={24}>
                <Upload onChange={handleEl} accept=".pdf, .png, .jpg, .jpeg" name="avatar" showUploadList={false}>
                  <div className="download-el">
                    {!expertise?.expertise_list?.final_exp_file ? 'Загрузить итоговый ЭЛ' : 'Заменить'}
                  </div>
                </Upload>
              </DownloadCol>
            </button>
          )}
        </GridItem>
      ),
    },
  ];

  return (
    <PagesContainerUI>
      <ExpertiseListModalComponent
        expertise_list={chosenExpertiseList}
        isPovs
        id={currentId}
        isRejected={!!currentPovCode}
        open={openModal}
        setOpen={(value) => setOpen(value)}
      />
      <BlueLayout padding="40px 0 20px 0">Экспертизы</BlueLayout>
      <FormBlock white noBorderRadius>
        <CustomTableContainer>
          <Table dataSource={currentExpertisesState} columns={expertisesColumns} />
        </CustomTableContainer>
      </FormBlock>
    </PagesContainerUI>
  );
};
